/**
 * Analysis numbers for the Sound tab: normalises the engine's analysis
 * object (prism_sound `analysis::analyze`, returned by the wasm renderer and
 * `prism sound render --analysis`) into one shape, and measures a rendered
 * buffer locally (ITU-R BS.1770-4 integrated loudness, peaks, onsets) when
 * the engine analysis is missing. Also the shared time-view helpers for the
 * waveform/spectrogram (zoom/scroll synced with the timeline playhead).
 */

import { isNum, isObj } from './param-values.js';

export interface AnalysisSummary {
  duration?: number;
  peakDb?: number;
  truePeakDb?: number;
  lufs?: number;
  shortTermMaxLufs?: number;
  lra?: number;
  rmsDb?: number;
  centroidHz?: number;
  pitchHz?: number;
  dcOffset?: number;
  clipped?: number;
  onsets: number[];
  pitchTrack: [number, number][];
  centroidTrack: [number, number][];
  /** Anything else numeric the engine reported (flattened keys). */
  extra: Record<string, number>;
  source: 'engine' | 'local';
}

function get(obj: unknown, path: string): unknown {
  let cur: unknown = obj;
  for (const k of path.split('.')) {
    if (!isObj(cur)) return undefined;
    cur = cur[k];
  }
  return cur;
}

function pickNum(obj: unknown, paths: string[]): number | undefined {
  for (const p of paths) {
    const v = get(obj, p);
    if (isNum(v)) return v;
  }
  return undefined;
}

function track(v: unknown, valueKeys: string[]): [number, number][] {
  if (!Array.isArray(v)) return [];
  const out: [number, number][] = [];
  for (const p of v) {
    if (Array.isArray(p) && isNum(p[0]) && isNum(p[1])) out.push([p[0], p[1]]);
    else if (isObj(p)) {
      const t = [p.t, p.time].find(isNum);
      const val = valueKeys.map((k) => p[k]).find(isNum);
      if (t !== undefined && val !== undefined) out.push([t, val]);
    }
  }
  return out;
}

function pickTrack(obj: unknown, paths: string[], valueKeys: string[]): [number, number][] {
  for (const p of paths) {
    const t = track(get(obj, p), valueKeys);
    if (t.length) return t;
  }
  return [];
}

const median = (xs: number[]) => {
  if (!xs.length) return undefined;
  const s = [...xs].sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)];
};

/** Normalises an engine analysis object (unknown field names tolerated). */
export function normalizeAnalysis(a: unknown): AnalysisSummary | null {
  if (!isObj(a)) return null;
  const pitchTrack = pickTrack(a, ['pitchTrack', 'pitch.track', 'pitch_track', 'pitch'], ['hz', 'freq', 'f0', 'pitch']);
  const centroidTrack = pickTrack(a, ['centroidTrack', 'centroid.track', 'spectralCentroid.track', 'centroid_track'], ['hz', 'centroid', 'value']);
  const onsetsRaw = get(a, 'onsets') ?? get(a, 'onset.times');
  const onsets = Array.isArray(onsetsRaw)
    ? onsetsRaw.map((o) => (isNum(o) ? o : isObj(o) ? [o.t, o.time].find(isNum) : undefined)).filter(isNum)
    : [];
  const s: AnalysisSummary = {
    duration: pickNum(a, ['duration', 'durationSeconds', 'length']),
    peakDb: pickNum(a, ['peakDb', 'peak_db', 'samplePeakDb', 'peak.db', 'peakDbfs']),
    truePeakDb: pickNum(a, ['truePeakDb', 'true_peak_db', 'truePeak', 'peak.truePeakDb', 'dbtp']),
    lufs: pickNum(a, ['lufs', 'integratedLufs', 'integrated_lufs', 'loudness.integrated', 'loudness.lufs', 'loudness']),
    shortTermMaxLufs: pickNum(a, ['shortTermMaxLufs', 'short_term_max', 'loudness.shortTermMax', 'loudness.shortTerm']),
    lra: pickNum(a, ['lra', 'loudnessRange', 'loudness.range', 'loudness.lra']),
    rmsDb: pickNum(a, ['rmsDb', 'rms_db', 'rms']),
    centroidHz: pickNum(a, ['centroidHz', 'centroid', 'spectralCentroid', 'centroid.mean', 'spectralCentroid.mean', 'centroid_hz']),
    pitchHz: pickNum(a, ['pitchHz', 'pitch.median', 'pitch.hz', 'medianPitchHz', 'f0', 'pitch_hz']) ?? median(pitchTrack.map((p) => p[1])),
    dcOffset: pickNum(a, ['dcOffset', 'dc_offset', 'dc']),
    clipped: pickNum(a, ['clipped', 'clippedSamples', 'clipping']),
    onsets,
    pitchTrack,
    centroidTrack,
    extra: {},
    source: 'engine',
  };
  const used = new Set(['duration', 'peakDb', 'truePeakDb', 'lufs', 'rmsDb', 'centroidHz', 'pitchHz', 'dcOffset', 'clipped']);
  const flat = (o: Record<string, unknown>, prefix: string, depth: number) => {
    for (const [k, v] of Object.entries(o)) {
      const key = prefix ? `${prefix}.${k}` : k;
      if (isNum(v) && !used.has(key)) s.extra[key] = v;
      else if (isObj(v) && depth < 2) flat(v, key, depth + 1);
    }
  };
  flat(a, '', 0);
  return s;
}

const toDb = (x: number) => (x > 0 ? 20 * Math.log10(x) : -Infinity);

/** BS.1770 K-weighting biquads for any sample rate (libebur128 design). */
export function kWeighting(rate: number): { b: [number, number, number]; a: [number, number, number] }[] {
  let f0 = 1681.974450955533;
  const G = 3.999843853973347;
  let Q = 0.7071752369554196;
  let K = Math.tan((Math.PI * f0) / rate);
  const Vh = Math.pow(10, G / 20);
  const Vb = Math.pow(Vh, 0.4996667741545416);
  let a0 = 1 + K / Q + K * K;
  const shelf = {
    b: [(Vh + (Vb * K) / Q + K * K) / a0, (2 * (K * K - Vh)) / a0, (Vh - (Vb * K) / Q + K * K) / a0] as [number, number, number],
    a: [1, (2 * (K * K - 1)) / a0, (1 - K / Q + K * K) / a0] as [number, number, number],
  };
  f0 = 38.13547087602444;
  Q = 0.5003270373238773;
  K = Math.tan((Math.PI * f0) / rate);
  a0 = 1 + K / Q + K * K;
  const hp = {
    b: [1, -2, 1] as [number, number, number],
    a: [1, (2 * (K * K - 1)) / a0, (1 - K / Q + K * K) / a0] as [number, number, number],
  };
  return [shelf, hp];
}

function filter(x: Float32Array, stages: ReturnType<typeof kWeighting>): Float64Array {
  let cur = Float64Array.from(x);
  for (const { b, a } of stages) {
    const out = new Float64Array(cur.length);
    let x1 = 0, x2 = 0, y1 = 0, y2 = 0;
    for (let i = 0; i < cur.length; i++) {
      const xi = cur[i]!;
      const y = b[0] * xi + b[1] * x1 + b[2] * x2 - a[1] * y1 - a[2] * y2;
      x2 = x1;
      x1 = xi;
      y2 = y1;
      y1 = y;
      out[i] = y;
    }
    cur = out;
  }
  return cur;
}

/** Integrated loudness (LUFS, gated) per ITU-R BS.1770-4; -Infinity for silence. */
export function integratedLoudness(channels: Float32Array[], rate: number): number {
  const stages = kWeighting(rate);
  const weighted = channels.map((c) => filter(c, stages));
  const len = weighted[0]?.length ?? 0;
  const block = Math.round(0.4 * rate);
  const step = Math.round(0.1 * rate);
  if (len < block) {
    // Shorter than one gating block: ungated mean square over the clip.
    let z = 0;
    for (const w of weighted) {
      let s = 0;
      for (let i = 0; i < len; i++) s += w[i]! * w[i]!;
      z += len ? s / len : 0;
    }
    return z > 0 ? -0.691 + 10 * Math.log10(z) : -Infinity;
  }
  const blocks: number[] = [];
  for (let start = 0; start + block <= len; start += step) {
    let z = 0;
    for (const w of weighted) {
      let s = 0;
      for (let i = start; i < start + block; i++) s += w[i]! * w[i]!;
      z += s / block;
    }
    blocks.push(z);
  }
  const loud = (z: number) => -0.691 + 10 * Math.log10(z);
  const abs = blocks.filter((z) => z > 0 && loud(z) > -70);
  if (!abs.length) return -Infinity;
  const rel = loud(abs.reduce((s, z) => s + z, 0) / abs.length) - 10;
  const gated = abs.filter((z) => loud(z) > rel);
  if (!gated.length) return -Infinity;
  return loud(gated.reduce((s, z) => s + z, 0) / gated.length);
}

/** Measures a rendered buffer in the browser (fallback when the engine analysis is absent). */
export function measureLocal(left: Float32Array, right: Float32Array | undefined, rate: number): AnalysisSummary {
  const chans = right && right.length ? [left, right] : [left];
  let peak = 0;
  let sq = 0;
  let dc = 0;
  let clipped = 0;
  let tp = 0;
  for (const c of chans) {
    for (let i = 0; i < c.length; i++) {
      const v = c[i]!;
      const a = Math.abs(v);
      if (a > peak) peak = a;
      if (a >= 0.9999) clipped++;
      sq += v * v;
      dc += v;
      // 4x Catmull-Rom interpolation between samples ≈ true peak.
      if (i > 0 && i + 2 < c.length) {
        const p0 = c[i - 1]!, p1 = v, p2 = c[i + 1]!, p3 = c[i + 2]!;
        for (const t of [0.25, 0.5, 0.75]) {
          const y = 0.5 * (2 * p1 + (-p0 + p2) * t + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t * t + (-p0 + 3 * p1 - 3 * p2 + p3) * t * t * t);
          if (Math.abs(y) > tp) tp = Math.abs(y);
        }
      }
    }
  }
  const n = chans.reduce((s, c) => s + c.length, 0) || 1;
  return {
    duration: left.length / rate,
    peakDb: toDb(peak),
    truePeakDb: toDb(Math.max(tp, peak)),
    lufs: integratedLoudness(chans, rate),
    rmsDb: toDb(Math.sqrt(sq / n)),
    dcOffset: dc / n,
    clipped,
    onsets: detectOnsets(chans, rate),
    pitchTrack: [],
    centroidTrack: [],
    extra: {},
    source: 'local',
  };
}

/** Energy-rise onset detector (10 ms frames, ≥ 6 dB jump above the recent average, 50 ms refractory). */
export function detectOnsets(chans: Float32Array[], rate: number): number[] {
  const frame = Math.max(1, Math.round(rate * 0.01));
  const len = chans[0]?.length ?? 0;
  const energy: number[] = [];
  for (let s = 0; s < len; s += frame) {
    let e = 0;
    for (const c of chans) for (let i = s; i < Math.min(len, s + frame); i++) e += c[i]! * c[i]!;
    energy.push(10 * Math.log10(e / frame + 1e-12));
  }
  const out: number[] = [];
  let last = -Infinity;
  for (let i = 1; i < energy.length; i++) {
    const from = Math.max(0, i - 8);
    let avg = 0;
    for (let j = from; j < i; j++) avg += energy[j]!;
    avg /= i - from;
    const t = (i * frame) / rate;
    if (energy[i]! > -60 && energy[i]! - avg >= 6 && t - last >= 0.05) {
      out.push(Math.round(t * 1000) / 1000);
      last = t;
    }
  }
  return out;
}

/** Engine analysis when present (filled in with local numbers it lacks), else local measurement. */
export function analysisFor(engine: unknown, left?: Float32Array, right?: Float32Array, rate = 48000): AnalysisSummary | null {
  const e = normalizeAnalysis(engine);
  if (!left) return e;
  const local = measureLocal(left, right, rate);
  if (!e) return local;
  const merged: AnalysisSummary = { ...local, ...Object.fromEntries(Object.entries(e).filter(([, v]) => v !== undefined)), source: 'engine' } as AnalysisSummary;
  if (!e.onsets.length) merged.onsets = local.onsets;
  return merged;
}

/* ------------------------------------------------------------------ */
/* Time view (zoom / scroll)                                           */

export interface TimeView {
  start: number;
  end: number;
}

/** Clamps a view into 0..duration keeping at least `minSpan` seconds visible. */
export function clampView(v: TimeView, duration: number, minSpan = 0.005): TimeView {
  const dur = Math.max(minSpan, duration);
  let span = Math.min(dur, Math.max(minSpan, v.end - v.start));
  let start = Math.max(0, Math.min(dur - span, v.start));
  if (!Number.isFinite(start)) start = 0;
  if (!Number.isFinite(span)) span = dur;
  return { start, end: start + span };
}

/** Zooms by `factor` (< 1 zooms in) around time `anchor`. */
export function zoomView(v: TimeView, factor: number, anchor: number, duration: number): TimeView {
  const span = (v.end - v.start) * factor;
  const f = v.end > v.start ? (anchor - v.start) / (v.end - v.start) : 0.5;
  return clampView({ start: anchor - f * span, end: anchor - f * span + span }, duration);
}

/** Scrolls by `dt` seconds. */
export function panView(v: TimeView, dt: number, duration: number): TimeView {
  return clampView({ start: v.start + dt, end: v.end + dt }, duration);
}

export function formatDb(v: number | undefined, unit = 'dB'): string {
  if (v === undefined) return '—';
  if (!Number.isFinite(v)) return `-∞ ${unit}`;
  return `${v.toFixed(1)} ${unit}`;
}

export function formatHz(v: number | undefined): string {
  if (v === undefined || !Number.isFinite(v)) return '—';
  return v >= 1000 ? `${(v / 1000).toFixed(2)} kHz` : `${v.toFixed(1)} Hz`;
}
